<header class="page-head">
  <div class="page-head__identity">
    <span class="page-head__icon"><i class="fa fa-phone"></i></span>
    <div>
      <h1 class="page-head__title">Novo Broadcast de Voz</h1>
      <p class="page-head__subtitle">Cinco passos: nome e lista, áudio, tentativas, agendamento e confirmação com o custo estimado.</p>
    </div>
  </div>
  <div class="page-head__actions">
    <a class="sf-btn sf-btn--secondary" routerLink="/broadcasts-call" data-testid="btn-back">
      <i class="fa fa-chevron-left"></i> Voltar
    </a>
  </div>
</header>

<div class="sf-card broadcast-call-card">
  <!-- Stepper -->
  <ol class="m8v-stepper" data-testid="stepper">
    <ng-container *ngFor="let label of steps; let i = index">
      <li [class.on]="currentStep === i + 1" [class.done]="currentStep > i + 1">
        <button
          type="button"
          [disabled]="currentStep !== i + 1 && !canGoTo(i + 1)"
          [class.editable]="canGoTo(i + 1)"
          (click)="goTo(i + 1)"
          [attr.data-testid]="'step-' + (i + 1)"
          [attr.aria-current]="currentStep === i + 1 ? 'step' : null"
        >
          <span class="n">
            <i class="fa fa-check" *ngIf="currentStep > i + 1; else stepNumber"></i>
            <ng-template #stepNumber>{{ i + 1 }}</ng-template>
          </span>
          <span class="lbl">{{ label }}</span>
          <i class="fa fa-pencil edit-ico" *ngIf="canGoTo(i + 1)"></i>
        </button>
      </li>
      <span class="line" *ngIf="i < steps.length - 1" [class.done]="currentStep > i + 1"></span>
    </ng-container>
  </ol>

  <div class="m8v-wiz" [class.m8v-wiz--single]="currentStep !== 5">
    <div>

      <!-- Passo 1: nome + lista -->
      <div *ngIf="currentStep === 1" data-testid="pane-1">
        <p class="section-hint">Nome e lista de contatos. A integração por postback é opcional.</p>

        <div class="field mb-3">
          <label for="bc-name">Nome do Broadcast <span class="text-danger">*</span></label>
          <input
            id="bc-name"
            type="text"
            class="form-control"
            [(ngModel)]="name"
            [maxlength]="nameMax"
            placeholder="Ex: Campanha de Agosto"
            data-testid="input-name"
          />
          <span class="field-hint">De 3 a {{ nameMax }} caracteres.</span>
        </div>

        <div class="field mb-3">
          <label>Lista de Contatos <span class="text-danger">*</span></label>
          <app-skeleton *ngIf="loadingLists" variant="text" [count]="1"
            label="Carregando as listas" data-testid="lists-loading"></app-skeleton>
          <app-search-select
            *ngIf="!loadingLists"
            [(ngModel)]="leadListId"
            [options]="listOptions"
            placeholder="Selecione uma lista"
            searchPlaceholder="Pesquisar lista…"
            emptyText="Nenhuma lista encontrada"
            noun="listas"
            testId="broadcast-call-list"
          ></app-search-select>
        </div>

        <h6 class="section-label mt-4">Integração (opcional)</h6>
        <div class="field">
          <label for="bc-postback">URL de Postback</label>
          <input
            id="bc-postback"
            type="url"
            class="form-control"
            [class.is-invalid]="postbackUrl && !isPostbackUrlValid()"
            [(ngModel)]="postbackUrl"
            placeholder="https://seu-endpoint.com/callback"
            data-testid="input-postback"
          />
          <span class="field-hint">Recebe o resultado de cada ligação (atendida, duração, DTMF).</span>
          <span class="field-error" *ngIf="postbackUrl && !isPostbackUrlValid()">
            <i class="fa fa-exclamation-circle"></i> URL inválida. Use um endereço HTTP ou HTTPS completo.
          </span>
        </div>
      </div>

      <!-- Passo 2: áudio -->
      <div *ngIf="currentStep === 2" data-testid="pane-2">
        <p class="section-hint">Qual áudio toca na ligação? Os áudios ficam em <strong>Ligações → Áudios</strong>.</p>

        <div class="field">
          <label>Áudio de Ligação <span class="text-danger">*</span></label>
          <app-skeleton *ngIf="loadingAudios" variant="text" [count]="1"
            label="Carregando os áudios" data-testid="audios-loading"></app-skeleton>
          <app-search-select
            *ngIf="!loadingAudios"
            [(ngModel)]="audioId"
            [options]="audioOptions"
            placeholder="Selecione um áudio"
            searchPlaceholder="Pesquisar áudio…"
            emptyText="Nenhum áudio encontrado"
            noun="áudios"
            testId="broadcast-call-audio"
          ></app-search-select>
        </div>

        <div class="m8v-audio-row" *ngIf="selectedAudio?.file_url" data-testid="audio-player">
          <audio controls preload="none" [src]="selectedAudio!.file_url"></audio>
          <span class="dur" *ngIf="selectedAudio?.duration_seconds">{{ formatDuration(selectedAudio!.duration_seconds) }}</span>
        </div>

        <div class="mt-3">
          <a class="sf-btn sf-btn--ghost sf-btn--sm" routerLink="/call4u-audios" data-testid="link-audios">
            <i class="fa fa-microphone"></i> Gerenciar áudios
          </a>
        </div>
      </div>

      <!-- Passo 3: tentativas -->
      <div *ngIf="currentStep === 3" data-testid="pane-3">
        <p class="section-hint">Quantas vezes tentar quem não atende, e com que intervalo.</p>

        <div class="inline-fields">
          <div class="field">
            <label>Número máximo de tentativas ({{ attemptsMin }}-{{ attemptsMax }})</label>
            <div class="m8v-num-field">
              <button type="button" (click)="changeAttempts(-1)" [disabled]="maxAttempts <= attemptsMin"
                aria-label="Menos uma tentativa" data-testid="attempts-minus">−</button>
              <input type="text" [value]="maxAttempts" readonly aria-label="Tentativas" data-testid="attempts-value" />
              <button type="button" (click)="changeAttempts(1)" [disabled]="maxAttempts >= attemptsMax"
                aria-label="Mais uma tentativa" data-testid="attempts-plus">+</button>
            </div>
          </div>

          <div class="field" *ngIf="maxAttempts > 1">
            <label for="bc-interval">Intervalo entre tentativas (minutos)</label>
            <input
              id="bc-interval"
              type="number"
              class="form-control"
              [class.is-invalid]="!isIntervalValid()"
              [(ngModel)]="retryIntervalMinutes"
              [min]="intervalMin"
              [max]="intervalMax"
              placeholder="Ex: 60"
              data-testid="input-interval"
            />
            <span class="field-hint">De {{ intervalMin }} a {{ intervalMax }} minutos.</span>
          </div>
        </div>

        <p class="field-hint mt-3">
          <ng-container *ngIf="maxAttempts === 1">1 tentativa: quem não atender não recebe nova ligação.</ng-container>
          <ng-container *ngIf="maxAttempts > 1">{{ maxAttempts }} tentativas, a cada {{ retryIntervalMinutes || '—' }} min: só para quem não atendeu nas anteriores.</ng-container>
        </p>
      </div>

      <!-- Passo 4: agendamento -->
      <div *ngIf="currentStep === 4" data-testid="pane-4">
        <p class="section-hint">Quando as ligações começam e até quando podem acontecer.</p>

        <app-send-when
          [window]="effectiveWindowCall"
          [sendNow]="sendNow"
          (sendNowChange)="setSendNow($event)"
          testId="schedule"
        >
          <div class="field-grid">
            <div class="field">
              <label>Data e Hora de Início <span class="text-danger">*</span></label>
              <app-window-datetime-picker
                [(ngModel)]="scheduledDate"
                [window]="effectiveWindowCall"
                testId="dt-start"
              ></app-window-datetime-picker>
            </div>
            <div class="field">
              <label>Data e Hora Final <span class="text-danger">*</span></label>
              <app-window-datetime-picker
                [(ngModel)]="scheduledEndDate"
                [window]="effectiveWindowCall"
                testId="dt-end"
              ></app-window-datetime-picker>
            </div>
          </div>
        </app-send-when>

        <div class="alert alert-danger call-notice mb-3" role="alert"
          *ngIf="!sendNow && stepErrors(4).length && scheduledDate && scheduledEndDate" data-testid="schedule-error">
          <i class="fa fa-exclamation-circle"></i>
          <span><ng-container *ngFor="let e of stepErrors(4); let last = last">{{ e }}<br *ngIf="!last" /></ng-container></span>
        </div>

        <ng-container *ngTemplateOutlet="windowNotice"></ng-container>
      </div>

      <!-- Passo 5: revisão -->
      <div *ngIf="currentStep === 5" data-testid="pane-5">
        <p class="section-hint">Revise seu broadcast. Confira os detalhes antes de confirmar o envio.</p>

        <div class="table-responsive">
          <table class="m8v-sum-table" data-testid="summary">
            <tbody>
              <tr>
                <th><i class="fa fa-tag ic"></i>Nome</th>
                <td>
                  {{ name }}
                  <button type="button" class="sf-btn sf-btn--ghost sf-btn--sm edit-link" (click)="goTo(1)" data-testid="edit-1">
                    <i class="fa fa-pencil"></i> editar
                  </button>
                </td>
              </tr>
              <tr>
                <th><i class="fa fa-users ic"></i>Lista de Contatos</th>
                <td>
                  {{ selectedList?.name || leadListId }}
                  <span class="confirmation-badge" *ngIf="selectedList?.leads_count != null">
                    {{ selectedList!.leads_count }} contatos
                  </span>
                  <button type="button" class="sf-btn sf-btn--ghost sf-btn--sm edit-link" (click)="goTo(1)" data-testid="edit-list">
                    <i class="fa fa-pencil"></i> editar
                  </button>
                </td>
              </tr>
              <tr>
                <th><i class="fa fa-volume-up ic"></i>Áudio</th>
                <td>
                  {{ selectedAudio?.title || audioId }}
                  <span class="confirmation-sub" *ngIf="selectedAudio?.duration_seconds">
                    ({{ formatDuration(selectedAudio!.duration_seconds) }})
                  </span>
                  <button type="button" class="sf-btn sf-btn--ghost sf-btn--sm edit-link" (click)="goTo(2)" data-testid="edit-2">
                    <i class="fa fa-pencil"></i> editar
                  </button>
                </td>
              </tr>
              <tr>
                <th><i class="fa fa-refresh ic"></i>Tentativas</th>
                <td>
                  <ng-container *ngIf="maxAttempts === 1">1x</ng-container>
                  <ng-container *ngIf="maxAttempts > 1">{{ maxAttempts }}x — a cada {{ retryIntervalMinutes }} min</ng-container>
                  <button type="button" class="sf-btn sf-btn--ghost sf-btn--sm edit-link" (click)="goTo(3)" data-testid="edit-3">
                    <i class="fa fa-pencil"></i> editar
                  </button>
                </td>
              </tr>
              <tr>
                <th><i class="fa ic" [ngClass]="sendNow ? 'fa-bolt' : 'fa-calendar'"></i>Agendamento</th>
                <td>
                  <ng-container *ngIf="sendNow">Envio Imediato</ng-container>
                  <ng-container *ngIf="!sendNow">
                    {{ formatLocal(scheduledDate) }}
                    <span class="confirmation-separator">até</span>
                    {{ formatLocal(scheduledEndDate) }}
                  </ng-container>
                  <button type="button" class="sf-btn sf-btn--ghost sf-btn--sm edit-link" (click)="goTo(4)" data-testid="edit-4">
                    <i class="fa fa-pencil"></i> editar
                  </button>
                </td>
              </tr>
              <tr *ngIf="postbackUrl">
                <th><i class="fa fa-link ic"></i>URL de Postback</th>
                <td class="text-break">
                  {{ postbackUrl }}
                  <button type="button" class="sf-btn sf-btn--ghost sf-btn--sm edit-link" (click)="goTo(1)" data-testid="edit-postback">
                    <i class="fa fa-pencil"></i> editar
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="mt-3">
          <ng-container *ngTemplateOutlet="windowNotice"></ng-container>
        </div>
      </div>
    </div>

    <!-- Custo estimado: só no passo 5, e só quando há preço de verdade. Informativo: não bloqueia. -->
    <aside class="m8v-wiz-side" *ngIf="currentStep === 5 && cost as c">
      <div class="sf-card m8v-cost-card" [class.danger]="c.short" data-testid="cost-card">
        <h5 class="section-title"><i class="fa fa-calculator"></i> Custo estimado</h5>

        <div class="big" data-testid="cost-max" *ngIf="c.billing === 'wallet'">
          até {{ c.maxDisplay | moneyMicros:'BRL':'pt-BR':2:2 }}
        </div>
        <div class="big" data-testid="cost-max" *ngIf="c.billing === 'credits'">
          até {{ formatInt(c.max) }} <small>{{ c.max === 1 ? 'crédito' : 'créditos' }}</small>
        </div>

        <div class="cost-lines">
          <div class="cost-line" *ngIf="c.billing === 'wallet'">
            <span>Tarifa</span>
            <b>{{ c.unitPriceMicros | moneyMicros:'BRL':'pt-BR' }} / bloco de 30 s</b>
          </div>
          <div class="cost-line">
            <span>Áudio de {{ formatDuration(selectedAudio!.duration_seconds) }}</span>
            <b *ngIf="c.billing === 'wallet'">{{ c.blocks }} {{ c.blocks === 1 ? 'bloco' : 'blocos' }} = {{ c.perCall | moneyMicros:'BRL':'pt-BR' }} por ligação atendida</b>
            <b *ngIf="c.billing === 'credits'">{{ c.blocks }} {{ c.blocks === 1 ? 'crédito' : 'créditos' }} por ligação atendida</b>
          </div>
          <div class="cost-line">
            <span>{{ formatInt(selectedList!.leads_count!) }} contatos × até {{ maxAttempts }} {{ maxAttempts === 1 ? 'tentativa' : 'tentativas' }}</span>
            <b>até {{ formatInt(c.maxDials) }} discagens</b>
          </div>
          <div class="cost-line" data-testid="cost-available">
            <span>{{ c.billing === 'wallet' ? 'Saldo disponível' : 'Créditos disponíveis' }}</span>
            <b *ngIf="c.billing === 'wallet'">{{ c.available | moneyMicros:'BRL':'pt-BR':2:2 }}</b>
            <b *ngIf="c.billing === 'credits'">{{ formatInt(c.available) }}</b>
          </div>
        </div>

        <p class="field-hint mt-2 mb-0" data-testid="cost-explain">
          Só ligações atendidas são cobradas, pelo áudio inteiro em blocos de 30 s. O valor acima é o teto:
          todos os contatos atendem. Se ninguém atender, nada é cobrado. As {{ maxAttempts }}
          {{ maxAttempts === 1 ? 'tentativa' : 'tentativas' }} só religam para quem não atendeu, então cada contato é cobrado no máximo uma vez.
        </p>
        <p class="field-hint mt-2 mb-0 cost-short" *ngIf="c.short" data-testid="cost-short">
          <i class="fa fa-exclamation-triangle"></i>
          O saldo pode não cobrir todas as ligações. Ao criar, você confirma se quer seguir com o envio parcial.
        </p>
        <p class="field-hint mt-2 mb-0">Estimativa: o valor cobrado sai do desfecho de cada ligação.</p>
      </div>
    </aside>
  </div>

  <!-- Navegação -->
  <div class="m8v-wiz-foot">
    <button type="button" class="sf-btn sf-btn--secondary" (click)="prev()" *ngIf="currentStep > 1" data-testid="btn-prev">
      <i class="fa fa-arrow-left"></i> Anterior
    </button>
    <span *ngIf="currentStep === 1"></span>

    <div class="right">
      <button
        type="button"
        class="sf-btn sf-btn--primary"
        (click)="next()"
        *ngIf="currentStep < totalSteps"
        [disabled]="!canProceed()"
        data-testid="btn-next"
      >
        Próximo <i class="fa fa-arrow-right"></i>
      </button>

      <button
        type="button"
        class="sf-btn sf-btn--primary"
        (click)="submit()"
        *ngIf="currentStep === totalSteps"
        [appLoadingButton]="isSaving"
        data-testid="btn-create"
      >
        <i class="fa fa-check"></i>
        Criar Broadcast
      </button>
    </div>
  </div>
</div>

<!-- Aviso da janela de ligação: SEMPRE montado a partir de `windowCall` (vai virar config). -->
<ng-template #windowNotice>
  <div class="alert alert-warning call-notice mb-0" role="note" *ngIf="windowCall" data-testid="window-notice">
    <i class="fa fa-moon-o"></i>
    <span>
      <strong>Atenção:</strong> ligações ficam bloqueadas entre {{ windowCall.stop }} e {{ windowCall.start }}
      (horário de Brasília). O que cair fora da janela é adiado para o próximo horário permitido.
    </span>
  </div>
</ng-template>
